/** Which end of a pair a drag is moving. */
export type MovedEdge = 'low' | 'high';

/**
 * The pair `next` would be if the edge the user moved had been mirrored
 * about the centre `current` sits on.
 *
 * Mirroring keeps the centre where the user put it while the extent
 * follows the drag — which is what makes widening a field of view a change
 * of size rather than a change of position.
 *
 * The ONLY thing an edge drag does. Moving the region is a separate
 * gesture on a separate part of the scout, so the two are composed rather
 * than chosen between: an edge drag that also shifted the region was a
 * second way to reach states these two already reach, at the cost of one
 * drag meaning two different things depending on a setting.
 *
 * `moved` is passed in rather than inferred from which value changed: the
 * caller holds the opposite edge where it was when the drag started, and
 * mirroring moves that edge, so the two drift apart as the drag proceeds
 * and the larger difference stops naming the edge the user has hold of.
 *
 * `limit`, where given, bounds the result: the half-extent is capped at
 * whatever fits on the tighter side, so the pair stays inside the limit
 * AND stays symmetric. Clamping the two edges separately would satisfy the
 * limit by breaking the symmetry the user asked for.
 */
export function mirrorAboutCentre(
    current: readonly [number, number],
    next: readonly [number, number],
    moved: MovedEdge,
    limit?: readonly [number, number]
): [number, number] {
    const centre = (current[0] + current[1]) / 2;
    let half = Math.abs((moved === 'low' ? next[0] : next[1]) - centre);
    if (limit) {
        // Floored at zero: a centre already outside the limit makes both
        // terms negative, and a negative half-extent returns a pair whose
        // low edge is above its high one.
        half = Math.max(
            0,
            Math.min(half, centre - limit[0], limit[1] - centre)
        );
    }
    return [centre - half, centre + half];
}

/**
 * `range` translated by `deltaMm`, held inside `limit`.
 *
 * The whole range moves or it stops at the limit; it is never squashed.
 * A move that reshaped the region would change the slice count, which is
 * the one thing a move is defined not to do.
 */
export function shiftWithin(
    range: readonly [number, number],
    deltaMm: number,
    limit: readonly [number, number]
): [number, number] {
    const room = limit[1] - limit[0] - (range[1] - range[0]);
    if (room < 0) {
        // Longer than the limit already: leave it rather than crop it.
        return [range[0] + deltaMm, range[1] + deltaMm];
    }
    const shifted = Math.min(
        Math.max(range[0] + deltaMm, limit[0]),
        limit[0] + room
    );
    return [shifted, shifted + (range[1] - range[0])];
}
